import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Button, Input, Select, Radio , Checkbox  } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import EligibilityConditionsBau from './EligibilityConditions/EligibilityConditionsBau';
import EligibilityConditionsLifecycle from './EligibilityConditions/EligibilityConditionsLifecycle';
import { hasDuplicateConditionField, isConditionValueMissing } from './EligibilityConditions/conditionOperators';

const { Option } = Select;

const StepEligibilityConditions = ({ onNext, onBack, formData, updateField }) => {
  const [errors, setErrors] = useState({});
  const [showAttributeValidation, setShowAttributeValidation] = useState(false);
  const [showTriggerWindowValidation, setShowTriggerWindowValidation] = useState(false);
  const selectedRuleType = formData?.ruleType || 'BAU';

  const hasEmptyAttributeValue = (groups) => {
    for (const group of groups || []) {
      const rows = [...(group.conditions || []), ...(group.afterConditions || [])];
      if (rows.some(isConditionValueMissing)) return true;
      if (group.children && hasEmptyAttributeValue(group.children)) return true;
    }
    return false;
  };

  const handleNext = () => {
    const nextErrors = {};

    // Dates are optional, but if one is set the other is required (scheduled rule).
    if (selectedRuleType === 'BAU') {
      nextErrors.activeStartDate = !formData.activeStartDate && Boolean(formData.activeEndDate);
      nextErrors.activeEndDate = Boolean(formData.activeStartDate) && !formData.activeEndDate;
      nextErrors.activeDateOrder = Boolean(formData.activeStartDate && formData.activeEndDate)
        && dayjs(formData.activeStartDate).isAfter(dayjs(formData.activeEndDate));
    }

    // Priority is a whole number greater than 0.
    nextErrors.priority = !(Number(String(formData.priority ?? '').trim()) > 0);
    // Either a limit greater than 0 or Unlimited must be chosen.
    nextErrors.redemptionLimit = !formData.redemptionLimitUnlimited
      && !(Number(String(formData.redemptionLimit ?? '').trim()) > 0);

    if (selectedRuleType === 'Lifecycle Triggered') {
      const triggerWindowDays = String(formData?.triggerWindowDays ?? formData?.triggerWindows?.[0]?.days ?? '').trim();
      nextErrors.triggerWindowDays = !triggerWindowDays;
    }

    const attributeConditionError = hasEmptyAttributeValue(formData?.eligibilityGroups)
      || hasDuplicateConditionField(formData?.eligibilityGroups);
    setShowAttributeValidation(true);
    setShowTriggerWindowValidation(true);
    setErrors(nextErrors);

    if (attributeConditionError || Object.values(nextErrors).some(Boolean)) return;
    onNext();
  };

  const handleFieldChange = (field, value) => {
    updateField(field, value);
    if (field === 'activeStartDate' || field === 'activeEndDate') {
      const startDate = field === 'activeStartDate' ? value : formData.activeStartDate;
      const endDate = field === 'activeEndDate' ? value : formData.activeEndDate;

      setErrors((current) => ({
        ...current,
        activeStartDate: !startDate && Boolean(endDate),
        activeEndDate: Boolean(startDate) && !endDate,
        activeDateOrder: Boolean(startDate && endDate) && dayjs(startDate).isAfter(dayjs(endDate)),
      }));
      return;
    }

    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: false }));
    }
  };

  return (
    <div className='step-container'>
      <div className='step-content'>
        <div className='step-header'>
          <div className='step-title'>Eligibility Conditions</div>
        </div>

        <div className='step-form d-flex d-flex-row justifyContentSpaceBetween border-bottom-light'>
          
          <div className='step-group w-50'>
            <div className='step-form-label'>Member Type</div>
            <div className='RadioGroup'>
              <Radio.Group
                value={formData?.memberType || 'Member'}
                onChange={(e) => updateField('memberType', e.target.value)}
              >
                <Radio.Button value='Member'>Member</Radio.Button>
                <Radio.Button value='Prive'>Privé</Radio.Button>
              </Radio.Group>
            </div>
          </div>
          <div className='step-group w-50'>
            <div className='step-form-label'>Rule Type</div>
            <div className='RadioGroup'>
              <Radio.Group
                value={selectedRuleType}
                onChange={(e) => updateField('ruleType', e.target.value)}
              >
                <Radio.Button value='BAU'>BAU</Radio.Button>
                <Radio.Button value='Lifecycle Triggered'>Lifecycle Triggered</Radio.Button>
              </Radio.Group>
            </div>
          </div>
        </div>

        <div className='step-content-body mt-16'>
          {selectedRuleType === 'Lifecycle Triggered' ? (
            <EligibilityConditionsLifecycle
              formData={formData}
              updateField={updateField}
              showValidation={showAttributeValidation}
              triggerWindowValidation={showTriggerWindowValidation}
              triggerWindowError={Boolean(errors.triggerWindowDays)}
            />
          ) : (
            <EligibilityConditionsBau
              formData={formData}
              updateField={handleFieldChange}
              errorStartDate={Boolean(errors.activeStartDate)}
              errorEndDate={Boolean(errors.activeEndDate)}
              errorDateOrder={Boolean(errors.activeDateOrder)}
              showValidation={showAttributeValidation}
            />
          )}
          <div className='step-group border-top-light pt-16 mt-16'>
            <div className='step-section-title Title-dark'>
              Redemption Limit <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <div className='step-subtitle mb-8'>
              How many times a single customer can use this rule.
            </div>
            <div className='step-group-body'>
              <div className='flex-body gap-16'>
                <div className='input-box w-120'>
                  <Input
                      placeholder="Enter Limit"
                      value={formData.redemptionLimit || ''}
                      inputMode='numeric'
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        if (/^\d*$/.test(rawValue)) {
                          handleFieldChange('redemptionLimit', rawValue);
                        }
                      }}
                      disabled={formData.redemptionLimitUnlimited}
                      status={errors.redemptionLimit ? 'error' : undefined}
                      className='Input-border w-100'
                    />
                  </div>
                  <div className='Checkbox-box'>
                    <Checkbox
                      checked={Boolean(formData.redemptionLimitUnlimited)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        handleFieldChange('redemptionLimitUnlimited', checked);
                        if (checked) handleFieldChange('redemptionLimit', '');
                        if (checked && errors.redemptionLimit) {
                          setErrors((current) => ({ ...current, redemptionLimit: false }));
                        }
                      }}
                    >
                      Unlimited
                    </Checkbox>
                  </div>
              </div>
              {errors.redemptionLimit && (
                <div className='step-form-hint' style={{ color: '#ff4d4f' }}>
                  Enter a Redemption Limit greater than 0 or select Unlimited.
                </div>
              )}
            </div>
          </div>
          <div className='step-group border-top-light pt-16 mt-16'>
            {/* <div className='step-section-title Title-dark'>
              Priority
            </div> */}
            
            <div className='step-group-body'>
              <div className='flex-body gap-16'>
                <div className='input-box w-100'>
                  <div className='step-form-label mb-8'>
                    <span className='font-600'>Priority</span> <span style={{ color: '#ff4d4f' }}>*</span>
                    <div className='step-subtitle mb-8'>
              Priority determines precedence when multiple rules could apply.
            </div>
                  </div>
                  <Input
                      placeholder="Enter Priority"
                      value={formData.priority || ''}
                      inputMode='numeric'
                      status={errors.priority ? 'error' : undefined}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        if (/^\d*$/.test(rawValue)) {
                          handleFieldChange('priority', rawValue);
                        }
                      }}
                      className='Input-border w-100'
                    />
                    {errors.priority && <div className='step-form-hint' style={{ color: '#ff4d4f' }}>Priority is required and must be greater than 0.</div>}
                    <div className='step-form-hint mt-2'>
                      Lower number = higher precedence when rules conflict. For example, Priority 1 will beat Priority 2.
                    </div>
                  </div>                  
              </div>
            </div>
          </div>
        </div>
        <div className='step-footer mt-32'>
          <Button onClick={() => onBack(1)} className='btn-s'>
            <ArrowLeftOutlined />
            Previous: Benefit & Mechanic
          </Button>
          <Button type="primary" onClick={handleNext} className='btn-p'>
            Next: Products (Include/Exclude)
            <ArrowRightOutlined />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepEligibilityConditions;
